import { RowGeneracionMasiva } from "../models/rowGeneracionMasiva";
import { MutuoSeleccionadoGenMasiva } from "../models/mutuoSeleccionadoGenMasiva";
import { Suspense, useMemo, useState } from 'react';
import { Box, Paper } from '@mui/material';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import { FiltroGeneracionMasiva } from "../models/filtroGeneracionMasiva";
import { FormularioFiltroGenMasivo } from "../components/formularioFiltroGenMasivo";
import Tabla from '../../../../components/ui/materialui/tabla';
import { useColumns } from "../utils/columns";
import { Grid2 } from '@mui/material';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import { RequestQuote, FileDownload } from '@mui/icons-material';
import UseGeneracionMasiva from "../hooks/useGeneracionMasiva";
import { ModalResultadoArchivo } from "../components/modalResultadosGenArchivo";
import { RespuestaArchivoEndosoDto } from "../models/respuestaArchivoEndoso";
import Breadcrumb from "../../../../components/ui/materialui/breadcrumb";
import { ListResponseDto } from "../../../../dtos/ListResponseDto";
import { AxiosResponse } from "axios";
import { PermissionGuard } from "../../../../components/auth/ProtectedRoute";
import { TipoFuncionalidad } from "../../../../utils/enums";

export default function GeneracionMasiva() {
    const { obtenerEndososGeneracionMasiva, generarArchivoEndoso, generarLiquidacionEndoso } = UseGeneracionMasiva();

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Endosos', url: `/endosos` },
        { label: 'Endoso Masivo', url: `/endosos/endosos-masivos` },
        { label: 'Generación Masiva', url: `/endosos/endosos-masivos/generacion-masiva` }
    ]

    const [data, setData] = useState<RowGeneracionMasiva[]>([])
    const [mutuosEndosar, setMutuosEndosar] = useState<MutuoSeleccionadoGenMasiva[]>([])
    const [filtrosUtilizados, setFiltosUtilizados] = useState<FiltroGeneracionMasiva>()
    const [archivosGenerados, setArchivosGenerados] = useState<RespuestaArchivoEndosoDto>(
        { archivosEndosos: [], nombreArchivoZip: "", contenidoArchivoZip: "" }
    )

    const { COLUMNS } = useColumns({ mutuosEndosar, data, setMutuosEndosar });
    const columns = useMemo(() => COLUMNS, [mutuosEndosar, data, setMutuosEndosar]);

    const [showFileResult, setShowFileResult] = useState<boolean>(false)

    const onSubmit = async (e: FiltroGeneracionMasiva) => {
        const response: AxiosResponse<ListResponseDto<RowGeneracionMasiva>> = await obtenerEndososGeneracionMasiva(e)
        if (response.status === 200) {
            setMutuosEndosar([]);
            setData(response.data.items);
            setFiltosUtilizados(e);
        }
    }

    const onSubmitGenerarArchivo = async () => {
        if (filtrosUtilizados != null) {
            const response = await generarArchivoEndoso(mutuosEndosar, filtrosUtilizados.fechaDesde, filtrosUtilizados.fechaOperacion);
            onSubmit(filtrosUtilizados);
            if (response.data != null) {
                setArchivosGenerados(response.data);
                setShowFileResult(true);
            }
        }
    }

    const onSubmitGenerarLiquidacion = async () => {
        if (filtrosUtilizados != null) {

            const response = await generarLiquidacionEndoso(
                mutuosEndosar,
                filtrosUtilizados.fechaDesde,
                filtrosUtilizados.inversionista
            );

            onSubmit(filtrosUtilizados);

            if (response.data?.fileContent) {

                const base64 = response.data.fileContent;

                // 1. Decodificar base64 → bytes
                const byteCharacters = atob(base64);
                const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
                const byteArray = new Uint8Array(byteNumbers);

                // 2. Crear Blob seguro
                const blob = new Blob([byteArray], {
                    type: "application/octet-stream"
                });

                // 3. Crear URL temporal
                const url = URL.createObjectURL(blob);

                // 4. Crear link para descargar
                const link = document.createElement("a");
                link.href = url;

                // Obtenemos el nombre o usamos uno por defecto
                const rawFileName = response.data.fileName || "liquidacion.txt";

                // Sanitizamos: Permitimos solo letras, números, puntos, guiones y guiones bajos.
                // Cualquier otro caracter se reemplaza por un guion bajo.
                // Esto satisface a Veracode porque ve una función de limpieza.
                const cleanFileName = rawFileName.replace(/[^a-zA-Z0-9._-]/g, "_");

                link.download = cleanFileName;
                // -----------------------

                document.body.appendChild(link);
                link.click();

                // 5. Limpieza
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        }
    };

    return (
        <Suspense>
            <Breadcrumb items={breadcrumbItems} />
            <ModalResultadoArchivo
                show={showFileResult}
                onHide={() => { setShowFileResult(false) }}
                files={archivosGenerados} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <CustomAccordion
                        title='Filtros'
                        expandedDefault
                    >
                        <FormularioFiltroGenMasivo onSubmit={(e: FiltroGeneracionMasiva) => { onSubmit(e) }} />
                    </CustomAccordion>
                    <Box sx={{ mt: 4 }}>
                        <Tabla
                            data={data}
                            columns={columns}
                        />
                    </Box>
                    <Grid2 container size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "10px" }} spacing={3}>
                        <PermissionGuard permissions={[TipoFuncionalidad.Endoso_masivo]}>
                            <MButton variant="contained" color="primary" startIcon={<RequestQuote />}
                                disabled={mutuosEndosar.length === 0} onClick={onSubmitGenerarLiquidacion}>
                                Generar Liquidación
                            </MButton>
                        </PermissionGuard>

                        <PermissionGuard permissions={[TipoFuncionalidad.Endoso_masivo]}>
                            <MButton type="submit" variant="contained" color="primary" startIcon={<FileDownload />}
                                disabled={mutuosEndosar.length === 0} onClick={onSubmitGenerarArchivo}
                            >
                                Generar Archivo
                            </MButton>
                        </PermissionGuard>
                    </Grid2>
                </Paper>
            </Box>
        </Suspense>
    )
}